// The Bearer gate for a consumer's own API routes, lifted from
// apps/corpus/worker/api/session.ts.
//
// The SPA fetches the broker JWT from `/auth/token` (the cookie is
// `/auth`-scoped on purpose) and presents it here as a Bearer header;
// verification is the same JWKS check the callback ran, so a token is
// re-verified on every API call.

import {
  AuthConfigError,
  resolveConfigFor,
  type AuthConfig,
  type AuthEnv,
  type ConsumerAppConfig,
} from "./config";
import {
  getRemoteJwks,
  verifyBrokerJwt,
  type SessionLane,
  type VerifiedSession,
} from "./verify";

/**
 * A verified Bearer session, or a refusal. On success `lane` is the token's
 * own `typ`, narrowed to the lanes the app declared: an app on the default
 * gets `lane: "user_session"` and nothing else, and an app that opted into
 * `reader_session` must branch on `lane` before it reads `claims`.
 */
export type SessionResult<L extends SessionLane = "user_session"> =
  | ({ ok: true } & Pick<
      Extract<VerifiedSession, { lane: L }>,
      "lane" | "claims"
    >)
  | { ok: false; reason: string };

/**
 * Verify the Bearer token on an API request. Failures are values with a
 * case-distinguishing reason for the caller's `auth_missing` event — never
 * throws.
 */
export async function requireSessionFor<L extends SessionLane = "user_session">(
  app: ConsumerAppConfig<L>,
  request: Request,
  env: AuthEnv,
): Promise<SessionResult<L>> {
  const header = request.headers.get("authorization");
  if (!header || !header.startsWith("Bearer ")) {
    return { ok: false, reason: "no_token" };
  }
  const jwt = header.slice("Bearer ".length).trim();
  if (!jwt) return { ok: false, reason: "no_token" };

  // `resolveConfigFor` throws on a half-set peer pair, and this function's
  // contract is that it never does: the caller's job is to answer 401 with a
  // reason, not to crash the app's API route over a deploy defect. The reason
  // is distinct from every verification reason, so the log line says which.
  let config: AuthConfig;
  try {
    config = resolveConfigFor(app, env, new URL(request.url).hostname);
  } catch (error) {
    if (!(error instanceof AuthConfigError)) throw error;
    return { ok: false, reason: `misconfigured:${error.reason}` };
  }
  const verified = await verifyBrokerJwt<L>(
    jwt,
    getRemoteJwks(config.brokerOrigin),
    {
      issuer: config.expectedIssuer,
      audience: config.expectedAudience,
      ...(app.lanes === undefined ? {} : { lanes: app.lanes }),
    },
  );
  if (!verified.ok) return { ok: false, reason: verified.reason };
  // Re-shaped from the narrowed result, so `lane` and `claims` stay paired —
  // a reader's claims can never arrive labelled as a user's.
  const { lane, claims } = verified;
  return { ok: true, lane, claims } as SessionResult<L>;
}
